/**
 * Mascaras de input client-side para os formularios do CRM.
 *
 * Convencao: cada funcao recebe a string "crua" (com ou sem mascara),
 * filtra digitos e devolve a versao mascarada. `maxLength` interno
 * descarta digitos a mais — assim o usuario pode colar valores ja
 * mascarados sem quebrar o input.
 *
 * Para enviar ao backend, use `onlyDigits()` — backend Zod tambem
 * normaliza, mas mandar digitos puros economiza payload.
 */

export function onlyDigits(value: string): string {
  return value.replace(/\D/g, '');
}

/** `999.999.999-99` */
export function maskCPF(value: string): string {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/** `99.999.999/9999-99` */
export function maskCNPJ(value: string): string {
  const d = onlyDigits(value).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12)
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** Aplica CPF ou CNPJ conforme o tipo. */
export function maskDocument(value: string, type: 'pf' | 'pj'): string {
  return type === 'pf' ? maskCPF(value) : maskCNPJ(value);
}

/** `99999-999` */
export function maskCEP(value: string): string {
  const d = onlyDigits(value).slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/**
 * Normaliza digitos de telefone removendo o codigo de pais do Brasil
 * caso presente (55 + DDD + numero = 12 ou 13 digitos comecando com 55).
 * Garante que o resultado tenha no maximo 11 digitos (DDD + numero).
 */
export function normalizePhoneDigits(value: string): string {
  let d = onlyDigits(value);
  // Remove codigo de pais: +55 ou 0055 prefixando um numero brasileiro
  // (12 digitos = 55 + DDD + fixo; 13 digitos = 55 + DDD + celular)
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) {
    d = d.slice(2);
  }
  return d.slice(0, 11);
}

/** `(99) 9999-9999` para fixo, `(99) 99999-9999` para celular. */
export function maskPhone(value: string): string {
  const d = normalizePhoneDigits(value);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10)
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/** `999.99999.99-9` */
export function maskPisPasep(value: string): string {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 8) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 10)
    return `${d.slice(0, 3)}.${d.slice(3, 8)}.${d.slice(8)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 8)}.${d.slice(8, 10)}-${d.slice(10)}`;
}

/**
 * Mascara aplicada conforme o tipo do contato (phone/whatsapp = telefone;
 * email = sem mascara; address = sem mascara — endereco tem seu proprio bloco).
 */
export function maskContactValue(
  value: string,
  type: 'phone' | 'email' | 'whatsapp' | 'address',
): string {
  if (type === 'phone' || type === 'whatsapp') return maskPhone(value);
  return value;
}

/** Lista das 27 UFs (igual `client-fields.dto.ts` no backend). */
export const UF_LIST = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO',
  'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI',
  'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
] as const;

export type UF = (typeof UF_LIST)[number];

/**
 * Mascara progressiva `DD/MM/AAAA`. Aplica `/` automaticamente conforme
 * o usuario digita. Aceita string ja mascarada (filtra digitos) e descarta
 * digitos a mais (>8) sem quebrar.
 */
export function maskDate(value: string): string {
  const d = onlyDigits(value).slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * Converte data ISO (YYYY-MM-DD) para DD/MM/AAAA. Strings que nao batem
 * voltam intactas — UI usa nativamente como fallback.
 */
export function isoToBrDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** Converte DD/MM/AAAA para ISO YYYY-MM-DD; retorna null se invalido. */
export function brDateToIso(br: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(br.trim());
  if (!m) return null;
  return `${m[3]}-${m[2]}-${m[1]}`;
}
